/* eslint-disable max-classes-per-file */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import type { getSourceDescription } from '@rover/rsdk/src/modules/Network/userAgent';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

/**
 * Not `source`: the eventstream stamps its own `source` (`javascript` / `ios` /
 * `android`) on every event.
 */
export type IntroVideoViewEntryPoint = 'card' | 'profile';

export type IntroVideoEventProperties = {
  entry_point: IntroVideoViewEntryPoint;
  video_url: string;
  sitter_opk: string;
  /** Null when logged out. */
  viewer_opk: string | null;
  cbsa: string | null;
  platform: ReturnType<typeof getSourceDescription>;
};

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when an owner plays a sitter's intro video, at most once per lightbox
 * open. Web and apps share this class so the names match across platforms.
 *
 * Neither emit path adds `platform`, so it is sent explicitly. `video_url` must
 * become a non-resolvable id before any non-US launch.
 *
 * &nbsp;
 */
export class IntroVideoViewEvent extends AnalyticsEvent {
  eventName = 'intro-video-view';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: IntroVideoEventProperties) {
    super(eventProperties, {
      entry_point: eventProperties.entry_point,
      platform: eventProperties.platform,
    });
  }
}

export type IntroVideoWatchedBucket = '0-25' | '25-50' | '50-75' | '75-95' | '95-100' | 'unknown';

/**
 * Bucketed so the Datadog tag carries six values rather than 101. 95% and up counts as the
 * whole video, so closing just before the end still reads as complete.
 */
export const toWatchedBucket = (percentWatched: number | null): IntroVideoWatchedBucket => {
  if (percentWatched === null) return 'unknown';
  if (percentWatched >= 95) return '95-100';
  if (percentWatched >= 75) return '75-95';
  if (percentWatched >= 50) return '50-75';
  if (percentWatched >= 25) return '25-50';
  return '0-25';
};

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the lightbox closes after an owner played a sitter's intro video,
 * reporting an estimate of how many distinct seconds of it played and whether
 * they scrubbed.
 *
 * &nbsp;
 */
export class IntroVideoWatchProgressEvent extends AnalyticsEvent {
  eventName = 'intro-video-watch-progress';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(
    eventProperties: IntroVideoEventProperties & {
      seconds_watched: number;
      duration_seconds: number | null;
      percent_watched: number | null;
      scrubbed: boolean;
    }
  ) {
    super(eventProperties, {
      entry_point: eventProperties.entry_point,
      platform: eventProperties.platform,
      watched_bucket: toWatchedBucket(eventProperties.percent_watched),
      scrubbed: eventProperties.scrubbed,
    });
  }
}
